import { type PaddingValue } from '../../../component/layout/header/HeaderLogo';
import { type AddressTypeInterface } from '../../../type/address.type';

// TODO: move this enum
export enum FrontendTag {
  AVA = 'AVA',
  BAGE = 'BAGE',
  BAGN = 'BAGN',
  EDIS = 'EDIS',
  EENG = 'EENG',
  EGD = 'EGD',
  EVNG = 'EVNG',
  ENG = 'ENG',
  ENAG = 'ENAG',
  HANG = 'HANG',
  HAW = 'HAW',
  LEW = 'LEW',
  LVN = 'LVN',
  MNSG = 'MNSG',
  RWW = 'RWW',
  SHNG = 'SHNG',
  SYNA = 'SYNA',
  VSEN = 'VSEN',
  WCON = 'WCON',
  WEVG = 'WEVG',
  WN = 'WN',
  WENS = 'WENS',
}

export interface FrontendConfig extends FrontendConfigBaseData {
  frontendTag: FrontendTag;
  name: string;
  links: FrontendConfigLinks;
  theme: FrontendConfigTheme;
  email: FrontendConfigEmail;
  print?: FrontendConfigPrint;
  registration: FrontendConfigRegistration;
  moreToolsLinks?: {
    crewCalendar?: FrontendConfigMoreToolsLinks;
    materialCatalogExternal?: string;
    materialCatalogExternalLabel?: string;
    materialInvoicing: FrontendConfigMoreToolsLinks;
  };
  purchaseOrganisation?: string;
}

interface FrontendConfigMoreToolsLinksByRole {
  prod: FrontendConfigMoreToolsUrlByRole;
  default: FrontendConfigMoreToolsUrlByRole;
}

interface FrontendConfigMoreToolsUrlByRole {
  contractor: string;
  default: string;
}

interface FrontendConfigMoreToolsLinksWithoutRole {
  prod: string;
  default: string;
}

type FrontendConfigMoreToolsLinks =
  FrontendConfigMoreToolsLinksByRole | FrontendConfigMoreToolsLinksWithoutRole;

interface FrontendConfigLinks {
  imprint: string;
  privacyPolicy: string;
  termsAndConditions?: string;
  sharepointLink?: string;
}

interface FrontendConfigEmail {
  color: string;
  contactEmail: string;
  name: string;
  logo: FrontendConfigLogo;
  imprintAddress: string;
  imprint: string;
}

interface FrontendConfigLogo {
  src: string;
  alt: string;
  height: string;
  width: string;
}

interface FrontendConfigPrint {
  logoHeight: string;
  address: string;
  imprint: string;
  hideAddressAndImprint?: boolean;
  hideLogo?: boolean;
}

interface FrontendConfigRegistration {
  contactClosingSignatureMessageId: string;
}

export interface FrontendConfigTheme {
  colors: FrontendConfigThemeColors;
  icons: {
    headerLogo: string;
    headerLogoWidth: number;
    headerLogoHeight: number;
    headerLogoPadding?: PaddingValue;
    footerLogo: string;
    footerLogoWidth: number;
    footerLogoHeight: number;
  };
}

export interface SharedConfigThemeColors {
  // Text colors
  colorTextPrimary: string;
  colorTextPrimaryDisabled: string;
  colorTextSecondary: string;
  colorTextTertiary: string;
  colorTextInverted: string;
  // Background colors
  colorBackgroundInteractive: string;
  colorBackgroundInteractiveHover: string;
  colorBackgroundInteractiveActive: string;
  colorBackgroundInteractiveDisabled: string;
  colorBackgroundDecorativeGrey: string;
  colorBackgroundDecorativeGreyLoaderOverlay: string;
  colorBackgroundDecorativeHover: string;
  colorBackgroundDecorativeInverted: string;
  colorBackgroundDecorativeWhite: string;
  // Icon colors
  colorIconInteractive: string;
  colorIconInteractiveDisabled: string;
  colorIconInteractiveHover: string;
  colorIconDecorative: string;
  colorIconInverted: string;
  colorIconException: string;
  colorIconWarning: string;
  // Graphic element colors
  colorGraphicDecorative: string;
  colorGraphicInteractive: string;
  colorGraphicInteractiveHover: string;
  colorGraphicInteractiveDisabled: string;
  // Shadow colors
  colorShadowDropdown: string;

  // Exceptions
  colorTrueBlack: string;
}

export type FrontendConfigThemeColors = SharedConfigThemeColors & {
  /* Color definitions for different companies can be found here:
   * https://confluence.eon.com/display/SMEXNET/Farbmatrix
   */

  // Primary colors

  /* Primary 0
   * Usage: Button active
   */
  colorPrimary0: string;
  /* Primary 1
   * Usage: Button hover
   */
  colorPrimary1: string;
  /* Primary 2
   * Usage: Main color for interactive elements
   */
  colorPrimary2: string;
  /* Primary 3
   * Usage: Focus color and color for horizontal bar above order list
   */
  colorPrimary3: string;
  /* Primary 4
   * Usage: Button/selector disabled
   */
  colorPrimary4: string;
  /* Primary 5
   * Usage: Light background
   */
  colorPrimary5: string;
  /*
   * Primary 50 overlay
   * used for background color DragAndDropArea
   */
  colorPrimary5Overlay: string;
  /* Primary 6
   * Usage: Light background hover
   */
  colorPrimary6: string;

  // Accents colors

  /* Error
   * Usage: Rejected state icon, red exclamation mark, primary notification color
   */
  colorAccentsError: string;
  /* Warning
   */
  colorAccentsWarning: string;
  /* Warning
   */
  colorAccentsSuccess: string;
  /* Highlight
   * Usage: Background of profile bubble in the upper right of the header
   */
  colorAccentsHighlight: string;

  // Text colors

  /* Text Primary Highlight
   */
  colorTextPrimaryHighlight: string;
  /* Text Error
   */
  colorTextError: string;

  // Error colors
  colorError: string;
  colorErrorHover: string;
  colorErrorActive: string;
  colorErrorDisabled: string;
  colorErrorBackground: string;

  // Warning colors
  colorWarning: string;
  colorWarningBackground: string;

  // Success colors
  colorSuccess: string;
  colorSuccessBackground: string;

  // Avatar Highlight colors

  /* Background Avatar
   * Usage: Background color for the avatar icon in the top right corner
   */
  colorBackgroundAvatarHighlight: string;

  /* Text Avatar Highlight
   * Usage: Text color for the avatar icon in the top right corner
   */
  colorTextAvatarHighlight: string;
};

export interface FrontendConfigBaseData {
  frontendTag: FrontendTag;
  domain: string;
  name: string;
  groupName?: string;
  contactEmail: string;
  address: AddressTypeInterface;
  districtCourt: string;
  commercialRegisterNumber: string;
  taxNumber: string;
  board: Array<string>;
  chairPerson: string;
  personResponsible: string;
}
